import React, { useState, useMemo } from 'react'
import { useCurrency } from '../context/CurrencyContext'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}
const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem',
  fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

function calcEMI(principal, annualRate, months) {
  if (!principal || !months) return 0
  if (!annualRate) return principal / months
  const r = annualRate / 12 / 100
  return principal * r * Math.pow(1 + r, months) / (Math.pow(1 + r, months) - 1)
}

function buildAmortization(principal, annualRate, months, emi) {
  const r = annualRate / 12 / 100
  const rows = []
  let balance = principal
  for (let i = 1; i <= months; i++) {
    const interest = balance * r
    const principalPaid = Math.min(emi - interest, balance)
    balance = Math.max(0, balance - principalPaid)
    rows.push({ month: i, emi: emi, interest, principal: principalPaid, balance })
    if (balance <= 0) break
  }
  return rows
}

export default function LoanCalculator() {
  const { fmt } = useCurrency()
  const [principal, setPrincipal] = useState('')
  const [rate, setRate] = useState('')
  const [tenure, setTenure] = useState('')
  const [tenureUnit, setTenureUnit] = useState('months')
  const [showFullTable, setShowFullTable] = useState(false)

  const months = tenureUnit === 'years' ? Math.round(parseFloat(tenure) * 12) : Math.round(parseFloat(tenure))
  const P = parseFloat(principal) || 0
  const R = parseFloat(rate) || 0

  const emi = useMemo(() => calcEMI(P, R, months), [P, R, months])
  const totalPayment = emi * months
  const totalInterest = totalPayment - P
  const interestPct = P > 0 ? ((totalInterest / totalPayment) * 100).toFixed(1) : 0

  const amortization = useMemo(() => {
    if (!P || !months || !emi) return []
    return buildAmortization(P, R, months, emi)
  }, [P, R, months, emi])

  const displayRows = showFullTable ? amortization : amortization.slice(0, 24)

  const ready = P > 0 && months > 0 && emi > 0

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1.5rem', alignItems: 'start' }}>

        {/* Input Panel */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>EMI Calculator</span>
          </div>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Loan Amount (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="e.g. 500000"
                value={principal} onChange={e => setPrincipal(e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
            <div>
              <label style={labelStyle}>Annual Interest Rate (%)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="e.g. 8.5"
                value={rate} onChange={e => setRate(e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
            <div>
              <label style={labelStyle}>Loan Tenure</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input style={{ ...inputStyle, flex: 1 }} type="text" inputMode="decimal" placeholder={tenureUnit === 'years' ? '5' : '60'}
                  value={tenure} onChange={e => setTenure(e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--text)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <select style={{ ...inputStyle, width: 'auto', flexShrink: 0 }}
                  value={tenureUnit} onChange={e => setTenureUnit(e.target.value)}>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {!ready ? (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
              Enter loan amount, rate, and tenure to see results
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                {[
                  { label: 'Monthly EMI', value: fmt(emi), color: '#c9a84c' },
                  { label: 'Total Payment', value: fmt(totalPayment), color: '#3d4a5c' },
                  { label: 'Total Interest', value: fmt(totalInterest), color: '#b8441a' },
                  { label: 'Interest Portion', value: interestPct + '%', color: '#8a8278' },
                ].map(c => (
                  <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
                    <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
                    <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.5rem', fontWeight: '700', color: 'var(--text)' }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* Principal vs Interest bar */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>Payment Breakdown</div>
                <div style={{ height: '12px', borderRadius: '99px', overflow: 'hidden', display: 'flex', marginBottom: '0.5rem' }}>
                  <div style={{ width: (P / totalPayment * 100) + '%', background: '#4a6741', transition: 'width 0.5s' }} />
                  <div style={{ flex: 1, background: '#b8441a' }} />
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', fontFamily: 'monospace', fontSize: '0.68rem' }}>
                  <span><span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: '#4a6741', marginRight: '0.3rem', verticalAlign: 'middle' }} />Principal {fmt(P)} ({(P / totalPayment * 100).toFixed(1)}%)</span>
                  <span><span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: '#b8441a', marginRight: '0.3rem', verticalAlign: 'middle' }} />Interest {fmt(totalInterest)} ({interestPct}%)</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Amortization Table */}
      {ready && amortization.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Amortization Schedule</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>{amortization.length} months total</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.72rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface-hdr)' }}>
                  {['Month', 'EMI', 'Principal', 'Interest', 'Balance'].map(h => (
                    <th key={h} style={{ padding: '0.6rem 1rem', textAlign: h === 'Month' ? 'left' : 'right', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.62rem', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, i) => (
                  <tr key={row.month} style={{ borderBottom: '1px solid var(--surface-hdr)', background: i % 2 === 0 ? 'transparent' : 'rgba(201,168,76,0.03)' }}>
                    <td style={{ padding: '0.5rem 1rem', color: 'var(--text-muted)' }}>{row.month}</td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: 'var(--text)' }}>{fmt(row.emi)}</td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#4a6741' }}>{fmt(row.principal)}</td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#b8441a' }}>{fmt(row.interest)}</td>
                    <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: 'var(--text)', fontWeight: '600' }}>{fmt(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {amortization.length > 24 && (
            <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--border)', background: 'var(--surface-hdr)', textAlign: 'center' }}>
              <button
                onClick={() => setShowFullTable(v => !v)}
                style={{ background: 'none', border: '1px solid var(--border)', padding: '0.35rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.65rem', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                {showFullTable ? `▲ Show less` : `▼ Show all ${amortization.length} months`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
